import { supabase, supabaseAdmin } from './supabase'

export interface CreateProfileData {
  user_id: string
  email: string | null
  username: string | null
}

export async function createUserProfile(data: CreateProfileData) {
  console.log('🔄 Tentando criar perfil:', data)
  
  try {
    // Primeiro, verificar se o perfil já existe
    const { data: existingProfile, error: checkError } = await supabase
      .from('profiles')
      .select('*')
      .eq('user_id', data.user_id)
      .maybeSingle()

    if (checkError) {
      console.error('❌ Erro ao verificar perfil existente:', checkError)
    }

    if (existingProfile) {
      console.log('✅ Perfil já existe:', existingProfile)
      return { data: existingProfile, error: null }
    }

    // Tentar criar o perfil com diferentes abordagens
    console.log('🔄 Criando novo perfil...')

    // Abordagem 1: Insert direto com client normal
    const { data: newProfile, error: insertError } = await supabase
      .from('profiles')
      .insert({
        user_id: data.user_id,
        email: data.email,
        username: data.username,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .select()
      .single()

    if (!insertError && newProfile) {
      console.log('✅ Perfil criado com insert:', newProfile)
      return { data: newProfile, error: null }
    }

    console.log('⚠️ Insert falhou, tentando upsert:', insertError)

    // Abordagem 2: Upsert
    const { data: upsertProfile, error: upsertError } = await supabase
      .from('profiles')
      .upsert({
        user_id: data.user_id,
        email: data.email,
        username: data.username,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'user_id'
      })
      .select()
      .single()

    if (!upsertError && upsertProfile) {
      console.log('✅ Perfil criado com upsert:', upsertProfile)
      return { data: upsertProfile, error: null }
    }

    console.log('⚠️ Upsert falhou, tentando com Admin Client:', upsertError)

    // Abordagem 3: Tentar com Admin Client (se disponível)
    if (supabaseAdmin) {
      try {
        console.log('🔄 Tentando com Admin Client...')
        const { data: adminProfile, error: adminError } = await supabaseAdmin
          .from('profiles')
          .insert({
            user_id: data.user_id,
            email: data.email,
            username: data.username,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .select()
          .single()

        if (!adminError && adminProfile) {
          console.log('✅ Perfil criado com Admin Client:', adminProfile)
          return { data: adminProfile, error: null }
        }
        
        console.log('⚠️ Admin Client falhou:', adminError)
      } catch (adminErr) {
        console.log('⚠️ Admin Client não disponível:', adminErr)
      }
    } else {
      console.log('⚠️ Admin Client não configurado')
    }

    // Abordagem 4: Tentar via RPC (se existir uma função)
    try {
      const { data: rpcProfile, error: rpcError } = await supabase
        .rpc('create_profile_if_not_exists', {
          p_user_id: data.user_id,
          p_email: data.email,
          p_username: data.username
        })

      if (!rpcError && rpcProfile) {
        console.log('✅ Perfil criado via RPC:', rpcProfile)
        return { data: rpcProfile, error: null }
      }
      
      console.log('⚠️ RPC não disponível ou falhou:', rpcError)
    } catch (rpcErr) {
      console.log('⚠️ RPC não disponível:', rpcErr)
    }

    // Se chegou até aqui, todas as abordagens falharam
    console.error('❌ Todas as tentativas de criar perfil falharam')
    console.error('Insert error:', insertError)
    console.error('Upsert error:', upsertError)

    return { 
      data: null, 
      error: insertError || upsertError || new Error('Falha ao criar perfil') 
    }

  } catch (error) {
    console.error('❌ Erro inesperado ao criar perfil:', error)
    return { data: null, error: error as Error }
  }
}

export async function getOrCreateProfile(userId: string, email?: string, username?: string) {
  console.log('🔄 Obtendo ou criando perfil para:', userId)
  
  try {
    // Primeiro tentar buscar
    const { data: existingProfile, error: fetchError } = await supabase
      .from('profiles')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle()

    if (existingProfile) {
      console.log('✅ Perfil encontrado:', existingProfile)
      return { data: existingProfile, error: null }
    }

    if (fetchError && fetchError.code !== 'PGRST116') {
      console.error('❌ Erro ao buscar perfil:', fetchError)
      return { data: null, error: fetchError }
    }

    // Se não existe, criar
    console.log('🔄 Perfil não encontrado, criando...')
    return await createUserProfile({
      user_id: userId,
      email: email || null,
      username: username || null
    })

  } catch (error) {
    console.error('❌ Erro inesperado:', error)
    return { data: null, error: error as Error }
  }
}
